import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Lock, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    try {
      // Pass the current origin URL so the reset link points to the correct environment
      await axios.post(`${API}/auth/forgot-password`, { 
        email,
        origin_url: window.location.origin
      });
      setEmailSent(true);
      toast.success('Check your email for reset instructions');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setResetComplete(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Show reset form if token is present
  if (token) {
    if (resetComplete) {
      return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 rounded-full bg-[#08D9D6]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-[#08D9D6]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Password Reset!</h1>
            <p className="text-[#A1A1AA] mb-6">
              Your password has been reset successfully. You can now login with your new password.
            </p>
            <Button
              onClick={() => navigate('/auth')}
              className="w-full bg-[#FF2E63] hover:bg-[#E01E50]"
            >
              Go to Login
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/auth')}
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to login
          </button>

          <div className="glass rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#FF2E63]/20 flex items-center justify-center">
                <Lock className="w-6 h-6 text-[#FF2E63]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Create New Password</h1>
                <p className="text-sm text-[#A1A1AA]">Enter your new password below</p>
              </div>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="new-password-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="confirm-password-input"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-password-btn"
                className="w-full bg-[#FF2E63] hover:bg-[#E01E50]"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Request reset form
  if (emailSent) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-[#08D9D6]/20 flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-[#08D9D6]" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Check Your Email</h1>
          <p className="text-[#A1A1AA] mb-6">
            If an account exists for <span className="text-white">{email}</span>, we've sent password reset instructions.
          </p>
          <Button
            variant="ghost"
            onClick={() => setEmailSent(false)}
            className="text-[#A1A1AA] hover:text-white"
          >
            Try a different email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/auth')}
          className="flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to login
        </button>

        <div className="glass rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#FF2E63]/20 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#FF2E63]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Forgot Password?</h1>
              <p className="text-sm text-[#A1A1AA]">We'll send you reset instructions</p>
            </div>
          </div>

          <form onSubmit={handleRequestReset} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Email Address</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="bg-white/5 border-white/10 text-white"
                data-testid="reset-email-input"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="send-reset-btn"
              className="w-full bg-[#FF2E63] hover:bg-[#E01E50]"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
